// Express middleware to validate user session
import { type Response, type NextFunction, Request } from 'express';
import dayjs from 'dayjs';
import { timingSafeEqual } from 'crypto';
import { escapeRegex } from '../../utils/escapeRegex';

const isServiceTokenValid = (authorizationHeader?: string): boolean => {
	const serviceToken = process.env['BACKOFFICE_SERVICE_TOKEN'];

	if (!serviceToken || !authorizationHeader) {
		return false;
	}
	const provided = new Uint8Array(Buffer.from(authorizationHeader));
	const expected = new Uint8Array(Buffer.from(serviceToken));
	if (provided.length !== expected.length) {
		return false;
	}
	return timingSafeEqual(provided, expected);
};

declare module 'express-session' {
	export interface SessionData {
		userId: string;
	}
}

export const auth = async (
	req: Request,
	res: Response,
	next: NextFunction
): Promise<void> => {
	const { db, logger } = req.app.locals;
	const { AUTH_COOKIE_NAME } = process.env;
	if (!AUTH_COOKIE_NAME) {
		throw new Error('Missing TravelPort API credentials');
	}
	if (!AUTH_COOKIE_NAME) {
		logger.error('AUTH_COOKIE_NAME not set');
		res.status(500).send('AUTH_COOKIE_NAME not set');
		return;
	}

	const Token = req.cookies[AUTH_COOKIE_NAME];
	if (Token && Token !== 'undefined') {
		let SessionId = '';
		if (Token && Token.includes(':')) {
			SessionId = Token.split(':')[1].split('.')[0];
		} else {
			SessionId = Token.split('.')[0];
		}
		SessionId = SessionId.replace('s%3A', '');

		const sessionDB = await db.collection('sessions').findOne(
			{
				_id: { $regex: escapeRegex(SessionId.toString()), $options: 'i' },
				expires: { $gte: dayjs().toDate() },
			},
			{ sort: { createdAt: -1 } }
		);

		if (sessionDB) {
			if (dayjs().diff(dayjs(sessionDB.expires), 'minutes') > 0) {
				res.status(401).json({ message: 'Unauthorized' });
				return;
			}

			req.session.userId = JSON.parse(sessionDB.session).userId;

			req.session.touch();
			next();
		} else {
			res.status(401).json({ message: 'Unauthorized' });
		}
	} else {
		if (isServiceTokenValid(req.headers.authorization)) {
			next();
		} else {
			res.status(401).json({ message: 'Unauthorized' });
		}
	}
};
